/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, of } from 'rxjs';
import { filter, withLatestFrom, map } from 'rxjs/operators';
import { LocalStorageHelper } from 'src/app/plain-javascript/services/LocalStorageHelper';
import * as _ from 'lodash-es';

export enum PreferencesStorePersistingStrategy {
	IN_MEMORY = 'in-memory',
	LOCAL_STORAGE = 'local-storage',
}

export interface IPreferencesStoreOptions {
	persistingStrategy?: PreferencesStorePersistingStrategy;
	storagePrefix?: string;
}

export type PreferencesType = string | boolean | number;
export type PreferenceValue = { [key: string]: PreferencesType };

//abstract class PreferencesStore<T extends {[key: string]: PreferencesType}> extends ComponentStore<T> {
abstract class PreferencesStore<T extends object> extends ComponentStore<T> {
	public preferences$: Observable<T> = this.select((state) => state);

	constructor(private defaultValues: T, private options?: IPreferencesStoreOptions) {
		super(defaultValues);
		// load data from local storage
		this.loadPersistedData(options?.persistingStrategy);
	}

	// this method is privated since we want to force the use of the updatePreferences and updatePreference effects
	private setPreferencesState = this.updater((state, preferences: Partial<T>) => ({
		...state,
		...preferences,
	}));

	private loadPersistedData(
		persistingStrategy: PreferencesStorePersistingStrategy = PreferencesStorePersistingStrategy.LOCAL_STORAGE
	): void {
		if (persistingStrategy === PreferencesStorePersistingStrategy.LOCAL_STORAGE) {
			this.updatePreferences(this.getDataFromLocalStorage(this.defaultValues));
		}
	}

	private persistData(
		preferences: Partial<T>,
		persistingStrategy: PreferencesStorePersistingStrategy = PreferencesStorePersistingStrategy.LOCAL_STORAGE
	): void {
		if (persistingStrategy === PreferencesStorePersistingStrategy.LOCAL_STORAGE) {
			this.storeDataToLocalStorage(preferences);
		}
	}

	private resetPreferencesState = this.updater(() => this.defaultValues);

	/**
	 *  This method wil update the whole preferences object
	 * 	It will check if inpit is different from current state
	 *  if it is then update state and store in localStorage if not then do nothing.
	 */
	private updatePreferences = this.effect<T>((updatedPreferences$) => {
		return updatedPreferences$.pipe(
			withLatestFrom(this.preferences$),
			tapResponse(
				([updatedPreferences, currentPreferences]: [T, T]) => {
					if (!updatedPreferences) {
						// if preferences are null then we reset the state to default values
						this.resetPreferencesState();
						this.persistData(this.defaultValues);
					} else if (!_.isEqual(currentPreferences, updatedPreferences)) {
						// Only update when current state is not equal to updated preferences
						this.setPreferencesState(updatedPreferences);
						this.persistData(updatedPreferences);
					}
				},
				(error) => {
					console.error(error);
				}
			)
		);
	});

	/**
	 *  This method will update a specific preference
	 *  It just accept a single node object
	 *  key: string - the preference key
	 *  value: PreferencesType - the preference value
	 */
	private updatePreference = this.effect<{ key: keyof T; value: PreferencesType }>((updatedPreference$) => {
		return updatedPreference$.pipe(
			withLatestFrom(this.preferences$),
			// only update if state is different of current value for the preference property
			filter(([preferenceValue, currentPreferences]) => {
				const { key, value } = preferenceValue;
				return currentPreferences[key] !== value;
			}),
			map(([preferenceValue]) => {
				const { key, value } = preferenceValue;
				return { [key]: value } as T;
			}),
			tapResponse(
				(preferenceValue: T) => {
					this.setPreferencesState(preferenceValue); // firebase
					this.persistData(preferenceValue); // local storage
				},
				(error) => {
					console.error(error);
				}
			)
		);
	});

	private getDataFromLocalStorage(preferences: T): T {
		return Object.entries(preferences).reduce((acc, [key]) => {
			acc[key] = LocalStorageHelper.read(`${this.options.storagePrefix}_${key}`);
			return acc;
		}, preferences as { [key: string]: PreferencesType }) as T;
	}

	private storeDataToLocalStorage(preferences: Partial<T>): void {
		Object.entries(preferences).forEach(([key, value]) => {
			LocalStorageHelper.write(`${this.options.storagePrefix}_${key}`, value);
		});
	}

	public getPreference(preferenceKey: keyof T): PreferencesType {
		return this.get()[preferenceKey] as PreferencesType;
	}

	public listenPreferenceValueChanges$(preferenceKey: keyof T) {
		return this.select((state) => state[preferenceKey]);
	}

	public setPreferences(preferences: T) {
		this.updatePreferences(preferences);
	}

	public setPreference(
		preferenceKey: keyof T,
		preferenceValue: PreferencesType | Observable<PreferencesType>
	) {
		const preference$ =
			preferenceValue instanceof Observable
				? preferenceValue.pipe(map((value) => ({ key: preferenceKey, value })))
				: of({ key: preferenceKey, value: preferenceValue });
		this.updatePreference(preference$);
	}
}

export default PreferencesStore;
